import { Box } from '@osmosis/react-ui/components/Box';
import { H4, P } from '@osmosis/react-ui/components/Text';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { FormattedMessage } from 'react-intl';
import {
  faFaceSmileRelaxed,
  faMeh,
  faSmile,
} from '@fortawesome/pro-light-svg-icons';
import React from 'react';

type ConfidenceLevel = {
  low: number;
  medium: number;
  high: number;
};

type Props = {
  color: string;
  confidence: ConfidenceLevel;
  sectionType: 'correct' | 'incorrect';
};

const ICONS = {
  low: faMeh,
  medium: faSmile,
  high: faFaceSmileRelaxed,
};

const CONFIDENCE_LEVELS = ['low', 'medium', 'high'] as const;

const LEVEL_LABELS = {
  low: { id: 'quiz.summary.confidence-low', defaultMessage: 'Low' },
  medium: { id: 'quiz.summary.confidence-medium', defaultMessage: 'Medium' },
  high: { id: 'quiz.summary.confidence-high', defaultMessage: 'High' },
};

/**
 * To render the quiz progress confidence summary.
 */
const QuizConfidenceSummary = ({ color, confidence, sectionType }: Props) => {
  return (
    <Box data-testid={`quiz-summary-confidence-${sectionType}`}>
      <P
        fontSize={16}
        color={color}
        mb={2}
        data-testid={`quiz-summary-${sectionType}`}
      >
        {sectionType === 'correct' ? (
          <FormattedMessage
            defaultMessage="Correct"
            id="quiz.summary.correct"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Incorrect"
            id="quiz.summary.incorrect"
          />
        )}
      </P>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'center',
          marginBottom: '10px',
        }}
      >
        {CONFIDENCE_LEVELS.map((level, index, arr) => (
          <React.Fragment key={level}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '80px',
              }}
            >
              <H4
                fontSize={16}
                color="#004D99"
                data-testid={`quiz-summary-confidence-${sectionType}-${level}`}
              >
                <FormattedMessage {...LEVEL_LABELS[level]} />
              </H4>

              <Icon
                icon={ICONS[level]}
                size="sm"
                style={{ height: '24px', width: '24px', margin: '10px 0' }}
                color={color}
                data-testid={`quiz-summary-icon-${sectionType}-${level}`}
              />

              <P
                fontSize={16}
                color="#004D99"
                data-testid={`quiz-summary-count-${sectionType}-${level}`}
              >
                {confidence[level]}
              </P>
            </Box>

            {index < arr.length - 1 && (
              <hr
                style={{
                  margin: '8px 0',
                  borderColor: color,
                  width: '80px',
                  height: '0px',
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default QuizConfidenceSummary;
